import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import confetti from 'canvas-confetti';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  styled,
  alpha,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ComputerIcon from '@mui/icons-material/Computer';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import SchoolIcon from '@mui/icons-material/School';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import GroupsIcon from '@mui/icons-material/Groups';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import StoreIcon from '@mui/icons-material/Store';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SelectableCard from '../components/common/SelectableCard';
import { useNavigate } from 'react-router-dom';

// Icons for "Heard About" options
import NewspaperIcon from '@mui/icons-material/Newspaper';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import MicIcon from '@mui/icons-material/Mic';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SearchIcon from '@mui/icons-material/Search';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';

// New Icons
import PaletteIcon from '@mui/icons-material/Palette';
import CodeIcon from '@mui/icons-material/Code';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import InterestsIcon from '@mui/icons-material/Interests';
// Icons for additional row
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import TheatersIcon from '@mui/icons-material/Theaters';
import WidgetsIcon from '@mui/icons-material/Widgets';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ExtensionIcon from '@mui/icons-material/Extension';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

const API_URL = process.env.REACT_APP_API_URL;

// Define colors from app.css for easier use
const appColors = {
  primary: '#4890fe',
  primaryDark: '#3a7ad9',
  primaryLight: 'rgba(72, 144, 254, 0.1)',
  textPrimary: '#2D3748',
  textSecondary: '#4A5568',
  textLight: '#718096',
  borderColor: 'rgba(0, 0, 0, 0.08)',
  backgroundLight: '#ffffff', // For elements on dark bg
  backgroundDark: '#f7f9fc', // Page background
  success: '#4caf50',
  error: '#f44336',
};

const LoadingScreen = ({ message = "Loading..." }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', bgcolor: appColors.backgroundDark }}>
    <CircularProgress sx={{ mb: 3, color: appColors.primary }} size={50} />
    <Typography variant="h6" color={appColors.textPrimary}>{message}</Typography>
  </Box>
);

const onboardingSteps = ['Connect LinkedIn', 'Your Details', 'Select Top Skills', 'How did you hear about us?', 'Confirm & Finish'];

const CustomStepConnector = styled(StepConnector)(({ theme }) => ({
  [`&.Mui-active .MuiStepConnector-line`]: {
    borderColor: appColors.primary,
  },
  [`&.Mui-completed .MuiStepConnector-line`]: {
    borderColor: appColors.primary,
  },
  [`& .MuiStepConnector-line`]: {
    borderColor: appColors.borderColor, // Use app's border color
    borderTopWidth: 2,
    borderRadius: 1,
  },
}));

const CustomStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  color: appColors.borderColor, // Default to border color
  display: 'flex',
  height: 22,
  alignItems: 'center',
  ...(ownerState.active && {
    color: appColors.primary,
  }),
  '& .CustomStepIcon-completedIcon': {
    color: appColors.primary,
    zIndex: 1,
    fontSize: 28,
  },
  '& .CustomStepIcon-circle': {
    width: 12,
    height: 12,
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    ...(ownerState.active && {
      width: 16,
      height: 16,
    }),
  },
}));

function CustomStepIcon(props) {
  const { active, completed, className } = props;
  return (
    <CustomStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <CheckCircleIcon className="CustomStepIcon-completedIcon" />
      ) : (
        <div className="CustomStepIcon-circle" />
      )}
    </CustomStepIconRoot>
  );
}

const industryOptions = [
  { value: 'technology', label: 'Technology', icon: <ComputerIcon /> },
  { value: 'web_development', label: 'Web Development', icon: <CodeIcon /> },
  { value: 'graphic_design', label: 'Graphic Design', icon: <PaletteIcon /> },
  { value: 'marketing_advertising', label: 'Marketing/Ads', icon: <StorefrontIcon /> },
  { value: 'ecommerce', label: 'E-commerce', icon: <ShoppingCartIcon /> },
  { value: 'consulting_services', label: 'Consulting', icon: <SupportAgentIcon /> },
  { value: 'travel_hospitality', label: 'Travel/Hospitality', icon: <FlightTakeoffIcon /> },
  { value: 'content_media', label: 'Content/Media', icon: <TheatersIcon /> },
  { value: 'finance_insurance', label: 'Finance/Insurance', icon: <AccountBalanceIcon /> },
  { value: 'healthcare', label: 'Healthcare', icon: <LocalHospitalIcon /> },
  { value: 'education', label: 'Education', icon: <SchoolIcon /> },
  { value: 'real_estate', label: 'Real Estate', icon: <HomeWorkIcon /> },
  { value: 'other', label: 'Other', icon: <EditIcon /> },
];

const roleOptions = [
  { value: 'founder_ceo', label: 'Founder / CEO', icon: <EmojiObjectsIcon /> },
  { value: 'manager_team_lead', label: 'Manager / Team Lead', icon: <SupervisorAccountIcon /> },
  { value: 'coach', label: 'Coach', icon: <RecordVoiceOverIcon /> },
  { value: 'trainer', label: 'Trainer', icon: <FitnessCenterIcon /> },
  { value: 'product_manager', label: 'Product Manager', icon: <WidgetsIcon /> },
  { value: 'hr_recruiter', label: 'HR / Recruiter', icon: <PersonAddAlt1Icon /> },
  { value: 'customer_support', label: 'Customer Support', icon: <ContactSupportIcon /> },
  { value: 'analyst', label: 'Analyst (Data/Biz)', icon: <QueryStatsIcon /> },
  { value: 'marketing_specialist', label: 'Marketing Specialist', icon: <CampaignIcon /> },
  { value: 'sales_representative', label: 'Sales Representative', icon: <TrendingUpIcon /> },
  { value: 'consultant_freelancer', label: 'Consultant / Freelancer', icon: <HandshakeIcon /> },
  { value: 'student', label: 'Student', icon: <AutoStoriesIcon /> },
  { value: 'other', label: 'Other', icon: <EditIcon /> },
];

const targetAudienceOptions = [
  { value: 'b2b_professionals', label: 'B2B Professionals', icon: <BusinessIcon /> },
  { value: 'b2c_consumers', label: 'B2C Consumers', icon: <PeopleOutlineIcon /> },
  { value: 'entrepreneurs_startups', label: 'Entrepreneurs/Startups', icon: <RocketLaunchIcon /> },
  { value: 'creators_influencers', label: 'Creators/Influencers', icon: <InterestsIcon /> },
  { value: 'non_profits_ngos', label: 'Non-profits/NGOs', icon: <VolunteerActivismIcon /> },
  { value: 'local_communities', label: 'Local Communities', icon: <LocationCityIcon /> },
  { value: 'niche_hobby_groups', label: 'Niche/Hobby Groups', icon: <ExtensionIcon /> },
  { value: 'healthcare_professionals_target', label: 'Healthcare Professionals', icon: <MedicalServicesIcon /> }, // Differentiate from industry
  { value: 'small_businesses', label: 'Small Businesses', icon: <StoreIcon /> },
  { value: 'enterprise_clients', label: 'Enterprise Clients', icon: <CorporateFareIcon /> },
  { value: 'students_academics', label: 'Students / Academics', icon: <MenuBookIcon /> },
  { value: 'job_seekers', label: 'Job Seekers', icon: <PersonSearchIcon /> },
  { value: 'other', label: 'Other', icon: <EditIcon /> },
];

const heardAboutOptions = [
  { value: 'news', label: 'In the news', icon: <NewspaperIcon /> },
  { value: 'friends_school', label: 'Friends or School', icon: <PeopleIcon /> },
  { value: 'work', label: 'From work', icon: <WorkIcon /> },
  { value: 'podcast', label: 'Podcast', icon: <MicIcon /> },
  { value: 'newsletter_blog', label: 'Newsletter or Blog', icon: <RssFeedIcon /> },
  { value: 'youtube', label: 'YouTube', icon: <YouTubeIcon /> },
  { value: 'linkedin', label: 'LinkedIn', icon: <LinkedInIcon /> },
  { value: 'tiktok', label: 'TikTok', icon: <SmartDisplayIcon /> },
  { value: 'google', label: 'Google', icon: <SearchIcon /> },
  { value: 'x_twitter', label: 'X (Twitter)', icon: <TwitterIcon /> },
  { value: 'instagram', label: 'Instagram', icon: <InstagramIcon /> },
  { value: 'facebook', label: 'Facebook', icon: <FacebookIcon /> },
  { value: 'dont_remember', label: 'Don\'t remember', icon: <HelpOutlineIcon /> },
  { value: 'other', label: 'Other', icon: <MoreHorizIcon /> },
];

const commonTimezones = [
  // North America
  { value: 'America/New_York', label: 'New York, Washington DC (UTC-5/-4)', region: 'North America' },
  { value: 'America/Chicago', label: 'Chicago, Dallas, Houston (UTC-6/-5)', region: 'North America' },
  { value: 'America/Denver', label: 'Denver, Phoenix (UTC-7/-6)', region: 'North America' },
  { value: 'America/Los_Angeles', label: 'Los Angeles, San Francisco (UTC-8/-7)', region: 'North America' },
  { value: 'America/Anchorage', label: 'Alaska (UTC-9/-8)', region: 'North America' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (UTC-10)', region: 'North America' },
  { value: 'America/Toronto', label: 'Toronto, Montreal (UTC-5/-4)', region: 'North America' },
  { value: 'America/Vancouver', label: 'Vancouver, Seattle (UTC-8/-7)', region: 'North America' },
  { value: 'America/Mexico_City', label: 'Mexico City (UTC-6/-5)', region: 'North America' },
  { value: 'America/Guatemala', label: 'Guatemala City (UTC-6)', region: 'North America' },
  { value: 'America/Costa_Rica', label: 'San José, Costa Rica (UTC-6)', region: 'North America' },
  { value: 'America/Panama', label: 'Panama City (UTC-5)', region: 'North America' },
  
  // South America
  { value: 'America/Sao_Paulo', label: 'São Paulo, Rio de Janeiro (UTC-3)', region: 'South America' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC-3)', region: 'South America' },
  { value: 'America/Santiago', label: 'Santiago, Chile (UTC-3/-4)', region: 'South America' },
  { value: 'America/Lima', label: 'Lima, Peru (UTC-5)', region: 'South America' },
  { value: 'America/Bogota', label: 'Bogotá, Colombia (UTC-5)', region: 'South America' },
  { value: 'America/Caracas', label: 'Caracas, Venezuela (UTC-4)', region: 'South America' },
  { value: 'America/La_Paz', label: 'La Paz, Bolivia (UTC-4)', region: 'South America' },
  { value: 'America/Montevideo', label: 'Montevideo, Uruguay (UTC-3)', region: 'South America' },
  { value: 'America/Asuncion', label: 'Asunción, Paraguay (UTC-3/-4)', region: 'South America' },
  { value: 'America/Guyana', label: 'Georgetown, Guyana (UTC-4)', region: 'South America' },
  
  // Europe
  { value: 'Europe/London', label: 'London, Edinburgh (UTC+0/+1)', region: 'Europe' },
  { value: 'Europe/Dublin', label: 'Dublin, Ireland (UTC+0/+1)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris, France (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin, Germany (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Rome', label: 'Rome, Milan (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Madrid', label: 'Madrid, Barcelona (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam, Netherlands (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Brussels', label: 'Brussels, Belgium (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Vienna', label: 'Vienna, Austria (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Zurich', label: 'Zurich, Switzerland (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Stockholm', label: 'Stockholm, Sweden (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Oslo', label: 'Oslo, Norway (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Copenhagen', label: 'Copenhagen, Denmark (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Helsinki', label: 'Helsinki, Finland (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Warsaw', label: 'Warsaw, Poland (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Prague', label: 'Prague, Czech Republic (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Budapest', label: 'Budapest, Hungary (UTC+1/+2)', region: 'Europe' },
  { value: 'Europe/Bucharest', label: 'Bucharest, Romania (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Athens', label: 'Athens, Greece (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Istanbul', label: 'Istanbul, Turkey (UTC+3)', region: 'Europe' },
  { value: 'Europe/Moscow', label: 'Moscow, Russia (UTC+3)', region: 'Europe' },
  { value: 'Europe/Kiev', label: 'Kyiv, Ukraine (UTC+2/+3)', region: 'Europe' },
  { value: 'Europe/Lisbon', label: 'Lisbon, Portugal (UTC+0/+1)', region: 'Europe' },
  
  // Asia Pacific
  { value: 'Asia/Tokyo', label: 'Tokyo, Osaka (UTC+9)', region: 'Asia Pacific' },
  { value: 'Asia/Seoul', label: 'Seoul, South Korea (UTC+9)', region: 'Asia Pacific' },
  { value: 'Asia/Shanghai', label: 'Beijing, Shanghai (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur, Malaysia (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Bangkok', label: 'Bangkok, Thailand (UTC+7)', region: 'Asia Pacific' },
  { value: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City, Vietnam (UTC+7)', region: 'Asia Pacific' },
  { value: 'Asia/Jakarta', label: 'Jakarta, Indonesia (UTC+7)', region: 'Asia Pacific' },
  { value: 'Asia/Manila', label: 'Manila, Philippines (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Taipei', label: 'Taipei, Taiwan (UTC+8)', region: 'Asia Pacific' },
  { value: 'Asia/Mumbai', label: 'Mumbai, Delhi, Bangalore (UTC+5:30)', region: 'Asia Pacific' },
  { value: 'Asia/Kolkata', label: 'Kolkata, Chennai (UTC+5:30)', region: 'Asia Pacific' },
  { value: 'Asia/Karachi', label: 'Karachi, Pakistan (UTC+5)', region: 'Asia Pacific' },
  { value: 'Asia/Dhaka', label: 'Dhaka, Bangladesh (UTC+6)', region: 'Asia Pacific' },
  { value: 'Asia/Colombo', label: 'Colombo, Sri Lanka (UTC+5:30)', region: 'Asia Pacific' },
  { value: 'Asia/Kathmandu', label: 'Kathmandu, Nepal (UTC+5:45)', region: 'Asia Pacific' },
  { value: 'Asia/Dubai', label: 'Dubai, UAE (UTC+4)', region: 'Asia Pacific' },
  { value: 'Asia/Qatar', label: 'Doha, Qatar (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Kuwait', label: 'Kuwait City (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Riyadh', label: 'Riyadh, Saudi Arabia (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Tehran', label: 'Tehran, Iran (UTC+3:30/+4:30)', region: 'Asia Pacific' },
  { value: 'Asia/Baghdad', label: 'Baghdad, Iraq (UTC+3)', region: 'Asia Pacific' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem, Israel (UTC+2/+3)', region: 'Asia Pacific' },
  { value: 'Asia/Beirut', label: 'Beirut, Lebanon (UTC+2/+3)', region: 'Asia Pacific' },
  { value: 'Asia/Damascus', label: 'Damascus, Syria (UTC+2/+3)', region: 'Asia Pacific' },
  { value: 'Asia/Amman', label: 'Amman, Jordan (UTC+2/+3)', region: 'Asia Pacific' },
  
  // Australia & Oceania
  { value: 'Australia/Sydney', label: 'Sydney, Canberra (UTC+10/+11)', region: 'Australia & Oceania' },
  { value: 'Australia/Melbourne', label: 'Melbourne (UTC+10/+11)', region: 'Australia & Oceania' },
  { value: 'Australia/Brisbane', label: 'Brisbane (UTC+10)', region: 'Australia & Oceania' },
  { value: 'Australia/Perth', label: 'Perth (UTC+8)', region: 'Australia & Oceania' },
  { value: 'Australia/Adelaide', label: 'Adelaide (UTC+9:30/+10:30)', region: 'Australia & Oceania' },
  { value: 'Australia/Darwin', label: 'Darwin (UTC+9:30)', region: 'Australia & Oceania' },
  { value: 'Pacific/Auckland', label: 'Auckland, New Zealand (UTC+12/+13)', region: 'Australia & Oceania' },
  { value: 'Pacific/Fiji', label: 'Suva, Fiji (UTC+12/+13)', region: 'Australia & Oceania' },
  { value: 'Pacific/Tahiti', label: 'Tahiti (UTC-10)', region: 'Australia & Oceania' },
  { value: 'Pacific/Guam', label: 'Guam (UTC+10)', region: 'Australia & Oceania' },
  
  // Africa
  { value: 'Africa/Cairo', label: 'Cairo, Egypt (UTC+2)', region: 'Africa' },
  { value: 'Africa/Lagos', label: 'Lagos, Nigeria (UTC+1)', region: 'Africa' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg, Cape Town (UTC+2)', region: 'Africa' },
  { value: 'Africa/Nairobi', label: 'Nairobi, Kenya (UTC+3)', region: 'Africa' },
  { value: 'Africa/Addis_Ababa', label: 'Addis Ababa, Ethiopia (UTC+3)', region: 'Africa' },
  { value: 'Africa/Dar_es_Salaam', label: 'Dar es Salaam, Tanzania (UTC+3)', region: 'Africa' },
  { value: 'Africa/Kampala', label: 'Kampala, Uganda (UTC+3)', region: 'Africa' },
  { value: 'Africa/Kigali', label: 'Kigali, Rwanda (UTC+2)', region: 'Africa' },
  { value: 'Africa/Accra', label: 'Accra, Ghana (UTC+0)', region: 'Africa' },
  { value: 'Africa/Abidjan', label: 'Abidjan, Ivory Coast (UTC+0)', region: 'Africa' },
  { value: 'Africa/Dakar', label: 'Dakar, Senegal (UTC+0)', region: 'Africa' },
  { value: 'Africa/Casablanca', label: 'Casablanca, Morocco (UTC+0/+1)', region: 'Africa' },
  { value: 'Africa/Tunis', label: 'Tunis, Tunisia (UTC+1)', region: 'Africa' },
  { value: 'Africa/Algiers', label: 'Algiers, Algeria (UTC+1)', region: 'Africa' },
  { value: 'Africa/Tripoli', label: 'Tripoli, Libya (UTC+2)', region: 'Africa' },
  { value: 'Africa/Khartoum', label: 'Khartoum, Sudan (UTC+2)', region: 'Africa' },
  { value: 'Africa/Kinshasa', label: 'Kinshasa, DR Congo (UTC+1)', region: 'Africa' },
  { value: 'Africa/Luanda', label: 'Luanda, Angola (UTC+1)', region: 'Africa' },
  { value: 'Africa/Maputo', label: 'Maputo, Mozambique (UTC+2)', region: 'Africa' },
  { value: 'Africa/Harare', label: 'Harare, Zimbabwe (UTC+2)', region: 'Africa' },
  { value: 'Africa/Lusaka', label: 'Lusaka, Zambia (UTC+2)', region: 'Africa' },
  { value: 'Indian/Mauritius', label: 'Port Louis, Mauritius (UTC+4)', region: 'Africa' },
];

// Confetti firing function (adapted from Dashboard.jsx)
const fireSideCannons = () => {
  const duration = 2500; // Changed to 2.5 seconds
  const animationEnd = Date.now() + duration;

  const confettiColors = [
    '#4890fe', // brand blue
    '#3a7ad9', // darker blue
    '#a864fd', // purple
    '#ff718d', // pink
    '#fdff6a', // yellow
    '#C0C0C0', // silver
    '#E63946'  // Vibrant Red
  ];

  const frame = () => {
    if (Date.now() > animationEnd) return; // Stop the animation after duration

    const perFrameCannonOptions = {
      particleCount: 7,
      angle: 60,
      spread: 55,
      startVelocity: 60,
      origin: { x: 0, y: 0.5 },
      colors: confettiColors,
      zIndex: 9999, // Ensure confetti is above other elements if necessary
    };

    // Left cannon
    confetti(perFrameCannonOptions);

    // Right cannon
    confetti({
      ...perFrameCannonOptions,
      angle: 120,
      origin: { x: 1, y: 0.5 },
    });

    requestAnimationFrame(frame);
  };

  frame(); // Start the animation loop
};

function OnboardingPage({ onOnboardingComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentDetailSubStep, setCurrentDetailSubStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [linkedInConnected, setLinkedInConnected] = useState(false);
  const [linkedInProfileData, setLinkedInProfileData] = useState(null);
  const [formData, setFormData] = useState({
    industry: '', customIndustry: '', role: '', customRole: '',
    targetAudience: '', customTargetAudience: '', heardAbout: '', customHeardAbout: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York', // Auto-detect user timezone
  });
  const [linkedInSkills, setLinkedInSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [customSkillInput, setCustomSkillInput] = useState('');
  const [userAddedSkills, setUserAddedSkills] = useState([]);
  const [skillLimitError, setSkillLimitError] = useState(false);

  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // Fire confetti on initial mount of OnboardingPage
  useEffect(() => {
    fireSideCannons();
  }, []); // Empty dependency array ensures it runs only once on mount

  const clearErrorAndSuccess = () => { setError(''); setSuccess(''); };

  const handleLinkedInConnect = useCallback(() => {
    clearErrorAndSuccess();
    setIsLoading(true);
    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
    if (!clientId) {
      setError('LinkedIn integration is not properly configured.');
      setIsLoading(false); return;
    }
    let redirectUri = process.env.REACT_APP_LINKEDIN_REDIRECT_URI;
    const state = Math.random().toString(36).substring(2, 8);
    sessionStorage.setItem('linkedin_auth_state', state); 
    localStorage.setItem('linkedin_auth_state', state);
    localStorage.removeItem('linkedin_last_error'); // Clear any previous LinkedIn errors
    const scopes = ['openid', 'profile', 'email', 'w_member_social', 'r_basicprofile'];
    const scope = encodeURIComponent(scopes.join(' '));
    const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${scope}&state=${state}`;
    window.location.href = linkedInAuthUrl;
  }, []);

  const fetchLinkedInProfileData = useCallback(async () => {
    setIsLoading(true); setError('');
    try {
      const response = await axios.get(`${API_URL}/api/linkedin/profile-data`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (response.data) setLinkedInProfileData(response.data);
    } catch (err) {
      // Check if error is due to LinkedIn profile already being connected to another account
      if (err.response?.data?.code === 'linkedin_already_connected') {
        setError('This LinkedIn profile is already connected to another account. Each LinkedIn profile can only be connected to one account.');
      } else {
        setError('Failed to fetch LinkedIn profile data.');
      }
    } finally { setIsLoading(false); }
  }, []);

  const checkLinkedInStatus = useCallback(async () => {
    setIsLoading(true); clearErrorAndSuccess();
    try {
      const response = await axios.get(`${API_URL}/api/auth/linkedin-status`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (response.data.connected) {
        setLinkedInConnected(true);
        setSuccess('LinkedIn is already connected. Fetching profile...');
        await fetchLinkedInProfileData();
        setCurrentStep(1);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) { 
      // Check if the error is because LinkedIn is already connected to another account
      if (error.response?.data?.code === 'linkedin_already_connected') {
        setError('This LinkedIn profile is already connected to another account. Each LinkedIn profile can only be connected to one account.');
      }
      // Otherwise silently fail as in original code
    }
    finally { setIsLoading(false); }
  }, [fetchLinkedInProfileData]);

  // Default skills to show when LinkedIn is not connected
  const defaultSkills = [
    'Marketing', 'Sales', 'Leadership', 'Management', 'Communication',
    'Social Media', 'Content Creation', 'Analytics', 'Project Management',
    'Customer Service', 'Design', 'Branding', 'SEO', 'Public Speaking',
    'Copywriting', 'Email Marketing', 'Strategy', 'Coaching', 'Teamwork',
    'Web Development', 'Product Management', 'Data Analysis'
  ];

  const fetchLinkedInSkills = useCallback(async () => {
    if (linkedInConnected) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/linkedin-skills`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (response.data.success && response.data.skills && response.data.skills.length > 0) {
          setLinkedInSkills(response.data.skills);
        } else {
          // If no skills were returned or empty array for connected LinkedIn user
          setLinkedInSkills(defaultSkills);
        }
      } catch (error) {
        console.error('Error fetching LinkedIn skills:', error);
        // If there was an error fetching skills, set default skills
        setLinkedInSkills(defaultSkills);
      }
    } else {
      // Not connected to LinkedIn, use default skills
      setLinkedInSkills(defaultSkills);
    }
  }, [linkedInConnected, defaultSkills]);

  const handleSkillSelect = (skill) => {
    setSkillLimitError(false);
    setSelectedSkills(prev => {
      if (prev.includes(skill)) {
        return prev.filter(s => s !== skill);
      }
      if (prev.length >= 5) {
        setSkillLimitError(true);
        return prev;
      }
      return [...prev, skill]; 
    });
  };

  const handleAddCustomSkill = () => {
    setSkillLimitError(false);
    const newSkill = customSkillInput.trim();
    if (newSkill && !userAddedSkills.includes(newSkill) && !linkedInSkills.includes(newSkill)) {
      setUserAddedSkills(prev => [...prev, newSkill]);
      if (selectedSkills.length < 5 && !selectedSkills.includes(newSkill)) {
        setSelectedSkills(prev => [...prev, newSkill]);
      } else if (selectedSkills.length >= 5 && !selectedSkills.includes(newSkill)) {
        setSkillLimitError(true);
      }
    }
    setCustomSkillInput('');
  };

  const handleSaveSkills = async () => {
    if (selectedSkills.length === 0) {
      setError('Please select at least one skill.');
      return;
    }
    if (selectedSkills.length > 5) {
      setError('You can select a maximum of 5 skills. Please deselect some.');
      return;
    }
    clearErrorAndSuccess();
    setSkillLimitError(false);
    try {
      await axios.post(`${API_URL}/api/auth/save-selected-skills`, 
        { selectedSkills, linkedInConnected },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setCurrentStep(3);
    } catch (error) {
      setError('Error saving selected skills');
    }
  };

  useEffect(() => {
    if (currentStep === 0 && !linkedInConnected) {
      checkLinkedInStatus();
    } else {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkLinkedInStatus, currentStep, linkedInConnected]); // Simplified dependencies

  // Check for LinkedIn connection error on page load
  useEffect(() => {
    // Check if there was a LinkedIn connection error in localStorage
    const linkedinError = localStorage.getItem('linkedin_last_error');
    if (linkedinError) {
      try {
        const errorObj = JSON.parse(linkedinError);
        if (errorObj.code === 'linkedin_already_connected') {
          setError('This LinkedIn profile is already connected to another account. Each LinkedIn profile can only be connected to one account.');
          // Use a more distinctive error to make it stand out
          setTimeout(() => {
            alert('LinkedIn Connection Error: This LinkedIn profile is already connected to another account. Each LinkedIn profile can only be connected to one account.');
          }, 500);
        } else {
          setError(errorObj.message || 'Error connecting to LinkedIn');
        }
      } catch (e) {
        setError(linkedinError || 'Error connecting to LinkedIn');
      }
      localStorage.removeItem('linkedin_last_error');
    }

    // Check for errors in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    if (error) {
      try {
        const errorObj = JSON.parse(decodeURIComponent(error));
        if (errorObj.code === 'linkedin_already_connected') {
          setError('This LinkedIn profile is already connected to another account. Each LinkedIn profile can only be connected to one account.');
          // Use a more distinctive error to make it stand out
          setTimeout(() => {
            alert('LinkedIn Connection Error: This LinkedIn profile is already connected to another account. Each LinkedIn profile can only be connected to one account.');
          }, 500);
        } else {
          setError(errorObj.message || 'Error connecting to LinkedIn');
        }
        // Store in localStorage for potential use by other components
        localStorage.setItem('linkedin_last_error', JSON.stringify(errorObj));
      } catch (e) {
        setError(error);
        localStorage.setItem('linkedin_last_error', error);
      }
      // Remove the error from URL to prevent showing it again on refresh
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  useEffect(() => {
    // Always fetch skills when entering the skills step
    if (currentStep === 2) {
      fetchLinkedInSkills();
    }
  }, [currentStep, fetchLinkedInSkills]);

  const getLabel = (optionsArray, value, customValue) => {
    if (value === 'other') return customValue || value;
    const option = optionsArray.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  const handleDetailsSubmit = async (e) => {
    if (e) e.preventDefault();
    clearErrorAndSuccess(); 
    setIsLoading(true);
    
    // Get the display labels instead of values
    const finalIndustry = getLabel(industryOptions, formData.industry, formData.customIndustry);
    const finalRole = getLabel(roleOptions, formData.role, formData.customRole);
    const finalTargetAudience = getLabel(targetAudienceOptions, formData.targetAudience, formData.customTargetAudience);

    if ((formData.industry === 'other' && !finalIndustry) || 
        (formData.role === 'other' && !finalRole) || 
        (formData.targetAudience === 'other' && !finalTargetAudience)) {
      setError('Please specify your choice for "Other" fields.');
      setIsLoading(false); 
      return;
    }

    if (!finalIndustry || !finalRole || !finalTargetAudience) {
      setError('Please make a selection for all fields.');
      setIsLoading(false); 
      return;
    }

    try {
      const detailsToSubmit = { 
        industry: finalIndustry, 
        role: finalRole, 
        targetAudience: finalTargetAudience,
        timezone: formData.timezone,
        linkedinProfileData: linkedInProfileData 
      };

      const response = await axios.post(
        `${API_URL}/api/auth/onboarding-details`, 
        detailsToSubmit, 
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        // Update user context with the returned user data
        if (updateUser && response.data.user) {
          updateUser({
            ...user,
            ...response.data.user
          });
        }

        // Update localStorage
        const storedUser = JSON.parse(localStorage.getItem('user'));
        if (storedUser) {
          localStorage.setItem('user', JSON.stringify({
            ...storedUser,
            industry: finalIndustry,
            professionalRole: finalRole,
            targetAudience: finalTargetAudience
          }));
        }

        setSuccess('Details saved!'); 
        setCurrentStep(2);
        setTimeout(() => setSuccess(''), 3000);
      } else { 
        setError(response.data.message || 'Failed to save details.'); 
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving details.');
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleCompleteOnboarding = async () => {
    clearErrorAndSuccess(); setIsLoading(true);
    try {
      const finalHeardAbout = formData.heardAbout === 'other' ? formData.customHeardAbout : formData.heardAbout;
      if (finalHeardAbout) {
        await axios.post(`${API_URL}/api/auth/heard-about-us`, 
          { heardAbout: finalHeardAbout },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
      }

      // Update has_completed_onboarding in the database only
      const completeOnboardingResponse = await axios.post(
        `${API_URL}/api/auth/complete-onboarding`,
        { has_completed_onboarding: true }, // Or 1, depending on backend
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setSuccess('Onboarding complete! Redirecting...');

      // Update user context
      if (updateUser && user) {
        const updatedUserFromResponse = completeOnboardingResponse.data.user; // Assuming backend returns updated user
        updateUser({
          ...user,
          ...updatedUserFromResponse, // Potentially merge with what backend returns
          preferences: {
            ...user.preferences,
            has_completed_onboarding: 1, // Explicitly set to 1 (true)
          },
        });
      }
      
      // Navigate to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard/home');
        if (onOnboardingComplete) { // Call if still provided, though redirect might be enough
            onOnboardingComplete();
        }
      }, 1500);

    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          (err.config?.url?.includes('heard-about-us') ? 'Error saving how you heard about us.' : 'Error completing onboarding.');
      setError(errorMessage); 
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleCardSelect = (field, value) => {
    setFormData(prev => ({
      ...prev, [field]: value,
      ...(field === 'industry' && value !== 'other' && { customIndustry: '' }),
      ...(field === 'role' && value !== 'other' && { customRole: '' }),
      ...(field === 'targetAudience' && value !== 'other' && { customTargetAudience: '' }),
      ...(field === 'heardAbout' && value !== 'other' && { customHeardAbout: '' }),
    }));
  };
  
  const nextDetailSubStep = () => {
    clearErrorAndSuccess();
    if (currentDetailSubStep === 0 && (!formData.industry || (formData.industry === 'other' && !formData.customIndustry))) {
      setError('Please select or specify your industry.'); return;
    }
    if (currentDetailSubStep === 1 && (!formData.role || (formData.role === 'other' && !formData.customRole))) {
      setError('Please select or specify your role.'); return;
    }
    if (currentDetailSubStep === 2 && (!formData.targetAudience || (formData.targetAudience === 'other' && !formData.customTargetAudience))) {
      setError('Please select or specify your target audience.'); return;
    }
    if (currentDetailSubStep === 3 && !formData.timezone) {
      setError('Please select your timezone.'); return;
    }
    if (currentDetailSubStep < 3) setCurrentDetailSubStep(prev => prev + 1);
    else handleDetailsSubmit(); 
  };

  const prevDetailSubStep = () => {
    clearErrorAndSuccess();
    if (currentDetailSubStep > 0) setCurrentDetailSubStep(prev => prev - 1);
    else setCurrentStep(0);
  };
  
  const renderStepContent = () => {
    if (isLoading && currentStep === 0 && !linkedInConnected) {
      return <LoadingScreen message="Initializing..." />;
    }
    if (isLoading && (success.includes('Fetching') || success.includes('LinkedIn is already connected'))) {
      return <LoadingScreen message={success} />;
    }

    const stepWrapperBaseSx = {
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      textAlign: 'center', width: '100%',
      px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 },
    };
    
    const primaryButtonSx = { bgcolor: appColors.primary, color: appColors.backgroundLight, '&:hover': { bgcolor: appColors.primaryDark }, borderRadius: '6px', textTransform: 'none', fontWeight: 500, fontSize: '0.95rem', py: 1.2, px: 3, boxShadow: 'none', '&:hover': { boxShadow: 'none'} }; 
    const outlineButtonSx = { 
      borderColor: appColors.borderColor, color: appColors.textSecondary, 
      borderWidth: '1px', borderStyle: 'solid', 
      '&:hover': { 
        borderColor: appColors.primary, 
        color: appColors.primary, 
        bgcolor: alpha(appColors.primary, 0.05)
      }, 
      borderRadius: '6px', textTransform: 'none', fontWeight: 500, 
      fontSize: '0.95rem', py: 1.2, px: 3, boxShadow: 'none'
    }; 

    const selectableCardSx = (isSelected) => ({
      textAlign: 'center', p: {xs: 1.5, sm: 2}, borderRadius: '8px', border: '1px solid',
      borderColor: isSelected ? appColors.primary : appColors.borderColor,
      backgroundColor: isSelected ? appColors.primaryLight : appColors.backgroundLight,
      boxShadow: 'none',
      transition: 'border-color 0.2s ease, background-color 0.2s ease',
      '&:hover': {
        borderColor: appColors.primary,
        backgroundColor: isSelected ? appColors.primaryLight : alpha(appColors.primaryLight, 0.5),
      },
      cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: {xs: 100, sm: 120},
    });
    const cardIconSx = { fontSize: {xs: '1.8rem', sm: '2rem'}, mb: 1, color: appColors.textSecondary };
    const cardLabelSx = (isSelected) => ({ 
      fontWeight: 500, fontSize: {xs: '0.85rem', sm: '0.9rem'}, 
      color: isSelected ? appColors.primary : appColors.textPrimary, mt: 0.5 
    });

    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ ...stepWrapperBaseSx, minHeight: '300px', justifyContent: 'center', position: 'relative' }}>
            <Typography variant="h4" gutterBottom sx={{ 
              mb: 1, 
              fontWeight: 'bold', 
              color: appColors.textPrimary,
              position: 'relative',
              zIndex: 1 // Ensure text is above confetti
            }}>
              Welcome to Instation!
            </Typography>
            <Typography 
              variant="body1" 
              color={appColors.textSecondary} 
              sx={{ 
                mb: 4, 
                maxWidth: '550px', 
                mx: 'auto',
                position: 'relative',
                zIndex: 1 // Ensure text is above confetti
              }}
            >
              Connect your LinkedIn to supercharge your experience.
            </Typography>
            {isLoading && !error && !success && (
              <CircularProgress sx={{mb:2, color: appColors.primary, position: 'relative', zIndex: 1}}/>
            )}
            {error && error.includes('LinkedIn profile is already connected') && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 4, 
                  width: '100%', 
                  maxWidth: '550px',
                  position: 'relative',
                  zIndex: 1,
                  border: '2px solid #f44336',
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  LinkedIn Connection Error
                </Typography>
                <Typography variant="body2">
                  {error}
                </Typography>
              </Alert>
            )}
            {!linkedInConnected && !isLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
                <Button 
                  color="inherit" 
                  sx={{
                    ...primaryButtonSx,
                    position: 'relative',
                    zIndex: 1 // Ensure button is above confetti
                  }} 
                  size="large" 
                  startIcon={<LinkedInIcon />} 
                  onClick={handleLinkedInConnect} 
                  disabled={isLoading}
                >
                  Connect LinkedIn
                </Button>
              </Box>
            )}
            {linkedInConnected && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, position: 'relative', zIndex: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 50, color: appColors.success, mb: 1 }} />
                <Typography variant="h6" sx={{fontWeight: 500, color: appColors.success }}>LinkedIn Connected!</Typography>
                <Typography color={appColors.textSecondary} sx={{mb: 1, fontSize: '1rem'}}>Moving to next step...</Typography>
              </Box>
            )}
          </Box>
        );
      case 1:
        let subStepTitle, subStepOptions, subStepField, subStepCustomField, subStepIconElement, isTimezoneStep = false;
        switch (currentDetailSubStep) {
          case 0: subStepTitle = 'What\'s your professional field?'; subStepOptions = industryOptions; subStepField = 'industry'; subStepCustomField = 'customIndustry'; subStepIconElement = <BusinessCenterIcon sx={{...cardIconSx, color: appColors.primary}} />; break;
          case 1: subStepTitle = 'Which role best describes you?'; subStepOptions = roleOptions; subStepField = 'role'; subStepCustomField = 'customRole'; subStepIconElement = <PersonPinIcon sx={{...cardIconSx, color: appColors.primary}} />; break;
          case 2: subStepTitle = 'Who are you trying to reach?'; subStepOptions = targetAudienceOptions; subStepField = 'targetAudience'; subStepCustomField = 'customTargetAudience'; subStepIconElement = <GroupsIcon sx={{...cardIconSx, color: appColors.primary}} />; break;
          case 3: subStepTitle = 'What\'s your timezone?'; isTimezoneStep = true; subStepIconElement = <ScheduleIcon sx={{...cardIconSx, color: appColors.primary}} />; break;
          default: return <Typography>Unknown detail step</Typography>;
        }
        if (isTimezoneStep) {
          // Create searchable options with region grouping
          const searchableTimezoneOptions = commonTimezones.map(tz => ({
            ...tz,
            searchText: `${tz.label} ${tz.region} ${tz.value}`.toLowerCase()
          }));

          return (
            <Box sx={stepWrapperBaseSx}>
              <Box textAlign="center" mb={3}>
                {subStepIconElement}
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: appColors.textPrimary }}>{subStepTitle}</Typography>
                <Typography variant="body1" color={appColors.textSecondary} sx={{ mb: 2 }}>
                  This helps us show you content and schedule posts at the right time
                </Typography>
              </Box>
              
              <Box sx={{ maxWidth: '500px', mx: 'auto', width: '100%' }}>
                <Autocomplete
                  options={searchableTimezoneOptions}
                  value={searchableTimezoneOptions.find(tz => tz.value === formData.timezone) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, timezone: newValue?.value || '' }));
                  }}
                  groupBy={(option) => option.region}
                  getOptionLabel={(option) => option.label}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const searchTerm = inputValue.toLowerCase();
                    return options.filter(option => 
                      option.searchText.includes(searchTerm)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and select your timezone"
                      placeholder="Type to search by country, city, or timezone..."
                      sx={{ 
                        '& .MuiOutlinedInput-root': { 
                          borderRadius: '6px', 
                          bgcolor: appColors.backgroundLight 
                        } 
                      }}
                    />
                  )}
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 'bold',
                          color: appColors.primary,
                          px: 2,
                          py: 1,
                          bgcolor: alpha(appColors.primaryLight, 0.3),
                          borderBottom: `1px solid ${appColors.borderColor}`
                        }}
                      >
                        {params.group}
                      </Typography>
                      <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                    </li>
                  )}
                  renderOption={(props, option) => (
                    <li {...props} style={{ paddingLeft: '24px' }}>
                      <Box sx={{ width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {option.label}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  noOptionsText="No timezone found. Try searching by country or city name."
                  sx={{
                    '& .MuiAutocomplete-listbox': {
                      maxHeight: '300px',
                    },
                    '& .MuiAutocomplete-groupLabel': {
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                    }
                  }}
                />

                {/* Show current time in selected timezone */}
                {formData.timezone && (
                  <Box sx={{ mt: 2, p: 2, borderRadius: '6px', bgcolor: appColors.primaryLight, textAlign: 'center' }}>
                    <Typography variant="body2" color={appColors.textSecondary}>
                      Current time in your timezone:
                    </Typography>
                    <Typography variant="h6" color={appColors.primary} sx={{ fontWeight: 500 }}>
                      {new Date().toLocaleString('en-US', { 
                        timeZone: formData.timezone,
                        weekday: 'long',
                        hour: '2-digit', 
                        minute: '2-digit',
                        timeZoneName: 'short'
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 4, width: '100%', maxWidth: '500px', mx: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button color="inherit" sx={outlineButtonSx} onClick={prevDetailSubStep} startIcon={<ArrowBackIcon />} disabled={isLoading}>Back</Button>
                <Button color="inherit" sx={primaryButtonSx} onClick={nextDetailSubStep} endIcon={<ArrowForwardIcon />} disabled={isLoading || !formData.timezone}>Next</Button>
              </Box>
            </Box>
          );
        }

        return (
          <Box sx={stepWrapperBaseSx}>
            <Box textAlign="center" mb={3}>
              {subStepIconElement}
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: appColors.textPrimary }}>{subStepTitle}</Typography>
            </Box>
            <Grid container spacing={2} justifyContent="center" alignItems="stretch" sx={{maxWidth: '900px'}}>
              {subStepOptions.map(opt => (
                <Grid item xs={6} sm={4} md={3} key={opt.value} sx={{ display: 'flex' }}>
                  <SelectableCard 
                    icon={React.cloneElement(opt.icon, { sx: {...cardIconSx, color: formData[subStepField] === opt.value ? appColors.primary : appColors.textSecondary} })}
                    label={opt.label}
                    value={opt.value}
                    isSelected={formData[subStepField] === opt.value}
                    onSelect={(val) => handleCardSelect(subStepField, val)}
                    fullWidth
                    sx={selectableCardSx(formData[subStepField] === opt.value)}
                    labelTypographyProps={{ sx: cardLabelSx(formData[subStepField] === opt.value) }}
                  />
                </Grid>
              ))}
            </Grid>
            {formData[subStepField] === 'other' && (
              <TextField fullWidth label={`Specify ${subStepField.replace('custom','').toLowerCase()}`} name={subStepCustomField}
                value={formData[subStepCustomField]} onChange={handleInputChange}
                sx={{ mt: 3, maxWidth: '500px', mx: 'auto', '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: appColors.backgroundLight } }} variant="outlined" required
              />
            )}
            <Box sx={{ mt: 4, width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button color="inherit" sx={outlineButtonSx} onClick={prevDetailSubStep} startIcon={<ArrowBackIcon />} disabled={isLoading}>Back</Button>
              <Button color="inherit" sx={primaryButtonSx} onClick={nextDetailSubStep} endIcon={<ArrowForwardIcon />} disabled={isLoading || (currentDetailSubStep === 0 && !formData.industry) || (currentDetailSubStep === 1 && !formData.role) || (currentDetailSubStep === 2 && !formData.targetAudience)}>Next</Button>
            </Box>
          </Box>
        );
      case 2:
        const allAvailableSkills = [...new Set([...linkedInSkills, ...userAddedSkills])].sort();
        return (
          <Box sx={stepWrapperBaseSx}>
            <Box textAlign="center" mb={3}>
              <EmojiObjectsIcon sx={{...cardIconSx, color: appColors.primary}} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: appColors.textPrimary }}>
                Select Your Top Skills
              </Typography>
              <Typography variant="body1" color={appColors.textSecondary} sx={{ mb: 2 }}>
                Choose up to 5 skills that best represent your expertise
              </Typography>
            </Box>
            <Box sx={{ mb: 2, width: '100%', maxWidth: '600px', display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                label="Add a custom skill"
                value={customSkillInput}
                onChange={(e) => setCustomSkillInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddCustomSkill();
                    e.preventDefault(); 
                  }
                }}
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: appColors.backgroundLight } }}
              />
              <Button 
                sx={{ // Explicitly define styles for this button to ensure text color
                  ...primaryButtonSx, 
                  py: 1, // Custom padding
                  px: 2, // Custom padding
                  color: appColors.backgroundLight // Ensuring white text from appColors
                }} 
                onClick={handleAddCustomSkill} 
                disabled={!customSkillInput.trim()}
              >
                Add
              </Button>
            </Box>
            {skillLimitError && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: '600px', width: '100%', borderRadius: '6px' }} onClose={() => setSkillLimitError(false)}>
                You can select a maximum of 5 skills.
              </Alert>
            )}
            
            <Grid container spacing={1.5} justifyContent="center" sx={{ maxWidth: '900px', mb: 3 }}>
              {allAvailableSkills.map((skill, index) => (
                <Grid item xs={6} sm={4} md={2.4} key={skill + '-' + index}>
                  <Chip
                    label={skill}
                    onClick={() => handleSkillSelect(skill)}
                    color={selectedSkills.includes(skill) ? "primary" : "default"}
                    variant={selectedSkills.includes(skill) ? "filled" : "outlined"}
                    sx={{
                      width: '100%',
                      height: '40px',
                      fontWeight: 500,
                      borderColor: selectedSkills.includes(skill) ? appColors.primary : appColors.borderColor,
                      bgcolor: selectedSkills.includes(skill) ? appColors.primary : appColors.backgroundLight,
                      color: selectedSkills.includes(skill) ? appColors.backgroundLight : appColors.textPrimary,
                      '&:hover': {
                        borderColor: appColors.primary,
                        bgcolor: selectedSkills.includes(skill) 
                          ? appColors.primaryDark
                          : alpha(appColors.primaryLight, 0.5)
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 4, width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                color="inherit" 
                sx={outlineButtonSx} 
                onClick={() => { clearErrorAndSuccess(); setCurrentStep(1);}} 
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <Button 
                color="inherit" 
                sx={primaryButtonSx} 
                onClick={handleSaveSkills}
                disabled={selectedSkills.length === 0 || selectedSkills.length > 5}
              >
                Next
              </Button>
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box sx={stepWrapperBaseSx}>
            <Box textAlign="center" mb={3}>
              <HelpOutlineIcon sx={{...cardIconSx, color: appColors.primary}} />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: appColors.textPrimary }}>How did you hear about us?</Typography>
            </Box>
            <Grid container spacing={1.5} justifyContent="center" alignItems="stretch" sx={{maxWidth: '900px'}}>
              {heardAboutOptions.map(opt => (
                <Grid item xs={6} sm={4} md={3} key={opt.value} sx={{ display: 'flex' }}>
                  <SelectableCard 
                    icon={React.cloneElement(opt.icon, { sx: {...cardIconSx, fontSize: {xs: '1.6rem', sm: '1.8rem'}, color: formData.heardAbout === opt.value ? appColors.primary : appColors.textSecondary} })}
                    label={opt.label}
                    value={opt.value}
                    isSelected={formData.heardAbout === opt.value}
                    onSelect={(val) => handleCardSelect('heardAbout', val)}
                fullWidth
                    sx={selectableCardSx(formData.heardAbout === opt.value)}
                    labelTypographyProps={{ sx: cardLabelSx(formData.heardAbout === opt.value) }}
                  />
                </Grid>
              ))}
            </Grid>
            {formData.heardAbout === 'other' && (
              <TextField fullWidth label="Please specify" name="customHeardAbout" value={formData.customHeardAbout} onChange={handleInputChange}
                sx={{ mt: 3, maxWidth: '500px', mx: 'auto', '& .MuiOutlinedInput-root': { borderRadius: '6px', bgcolor: appColors.backgroundLight } }} variant="outlined" required
              />
            )}
            <Box sx={{ mt: 4, width: '100%', maxWidth: '900px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button color="inherit" sx={outlineButtonSx} onClick={() => { clearErrorAndSuccess(); setCurrentDetailSubStep(2); setCurrentStep(1);}} startIcon={<ArrowBackIcon />} disabled={isLoading}>Back</Button>
              <Button color="inherit" sx={primaryButtonSx} onClick={() => { clearErrorAndSuccess(); if (!formData.heardAbout || (formData.heardAbout === 'other' && !formData.customHeardAbout)) { setError('Please make a selection.'); return; } setCurrentStep(4);}} endIcon={<ArrowForwardIcon />} disabled={isLoading || !formData.heardAbout || (formData.heardAbout === 'other' && !formData.customHeardAbout)}>Next</Button>
            </Box>
          </Box>
        );
      case 4:
        const displaySkills = selectedSkills || [];

        const industryLabel = getLabel(industryOptions, formData.industry, formData.customIndustry);
        const roleLabel = getLabel(roleOptions, formData.role, formData.customRole);
        const targetAudienceLabel = getLabel(targetAudienceOptions, formData.targetAudience, formData.customTargetAudience);
        const heardAboutLabel = getLabel(heardAboutOptions, formData.heardAbout, formData.customHeardAbout);
        const timezoneLabel = commonTimezones.find(tz => tz.value === formData.timezone)?.label || formData.timezone;

        return (
          <Box sx={{ ...stepWrapperBaseSx, textAlign: 'left', alignItems: 'flex-start' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: appColors.textPrimary, width: '100%', textAlign: 'center', mb: 3 }}>Confirm Your Details</Typography>
            <Box sx={{ p:2.5, borderRadius: '8px', border: `1px solid ${appColors.borderColor}`, bgcolor: appColors.backgroundLight, width: '100%', maxWidth: '650px', mx: 'auto', mb: 3}}>
              <Typography variant="h6" color={appColors.textPrimary} sx={{ mb: 1.5, fontWeight: 500 }}>LinkedIn Profile:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                {linkedInProfileData?.profile_picture && (
                  <Avatar 
                    src={linkedInProfileData.profile_picture} 
                    alt={linkedInProfileData?.name || 'Profile'} 
                    sx={{ width: 56, height: 56, mr: 2, border: `1px solid ${appColors.borderColor}` }}
                  />
                )}
                <Box>
                  <Typography variant="body1" fontWeight={500} color={appColors.textPrimary} sx={{ mb: 0.25 }}>{linkedInProfileData?.name || 'N/A'}</Typography>
                  <Typography variant="body2" color={appColors.textSecondary} sx={{ mb: 1.5 }}>{linkedInProfileData?.headline || 'N/A'}</Typography>
                </Box>
              </Box>
              
              <Typography variant="h6" color={appColors.textPrimary} sx={{ mb: 1, fontWeight: 500 }}>Your Top Skills:</Typography>
              {displaySkills.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5 }}>
                  {displaySkills.map((skill, index) => (
                    <Chip 
                      key={index} 
                      label={typeof skill === 'object' ? skill.name : skill} 
                      variant="outlined" 
                      size="small" 
                      sx={{borderColor: appColors.borderColor, color: appColors.textSecondary}} 
                    />
                  ))}
                </Box>
              ) : (
                <Typography sx={{ mb: 1.5, fontStyle: 'italic' }} variant="body2" color={appColors.textSecondary}>
                  No skills selected.
                </Typography>
              )}

              <Typography variant="h6" color={appColors.textPrimary} sx={{ mb: 1, fontWeight: 500, mt:1.5 }}>Your Selections:</Typography>
              {industryLabel && (
                <Typography variant="body2" color={appColors.textSecondary}>
                  Industry: {industryLabel}
                </Typography>
              )}
              {roleLabel && (
                <Typography variant="body2" color={appColors.textSecondary}>
                  Role: {roleLabel}
                </Typography>
              )}
              {targetAudienceLabel && (
                <Typography variant="body2" color={appColors.textSecondary}>
                  Target Audience: {targetAudienceLabel}
                </Typography>
              )}
              {timezoneLabel && (
                <Typography variant="body2" color={appColors.textSecondary}>
                  Timezone: {timezoneLabel}
                </Typography>
              )}
              {heardAboutLabel && (
                <Typography variant="body2" color={appColors.textSecondary}>
                  Heard About Us: {heardAboutLabel}
                </Typography>
              )}
            </Box>
            <Box sx={{ mt: 3, width: '100%', maxWidth: '650px', mx: 'auto', display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                color="inherit" 
                sx={outlineButtonSx} 
                onClick={() => { clearErrorAndSuccess(); setCurrentStep(3);}} 
                disabled={isLoading}
              >
                Back
              </Button>
              <Button 
                color="inherit" 
                sx={primaryButtonSx} 
                size="large" 
                onClick={handleCompleteOnboarding} 
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={22} color="inherit" /> : 'Complete Onboarding'}
              </Button>
            </Box>
          </Box>
        );
      default: return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ width: '100%', bgcolor: appColors.backgroundLight, borderRadius: '12px', p: {xs: 0, md:0}, boxShadow: `0 1px 4px ${alpha(appColors.borderColor, 0.25)}` }}>
        <Box sx={{px: {xs: 2, md: 3}, pt: {xs: 2, md:3}, pb: {xs:1, md:2} }}>
            <Stepper activeStep={currentStep} alternativeLabel connector={<CustomStepConnector />} sx={{ mb: {xs: 2, md: 3} }}>
            {onboardingSteps.map((label) => (
                <Step key={label}>
                <StepLabel StepIconComponent={CustomStepIcon} sx={{'.MuiStepLabel-label': {fontSize: '0.8rem', color: appColors.textSecondary, '&.Mui-active': {color: appColors.primary, fontWeight: 500}, '&.Mui-completed': {color: appColors.textPrimary, fontWeight: 500}} }}>{label}</StepLabel>
                </Step>
            ))}
            </Stepper>
        </Box>
        
        {error && !error.includes('LinkedIn profile is already connected') && 
          <Alert severity="error" sx={{ mx: {xs:2, md:3}, mb: 2, borderRadius: '6px' }} onClose={() => setError('')}>{error}</Alert>}
        {success && !isLoading && currentStep !== 0 && 
          <Alert severity="success" sx={{ mx: {xs:2, md:3}, mb: 2, borderRadius: '6px' }} onClose={() => setSuccess('')}>{success}</Alert>}
        
        <Box sx={{flexGrow: 1, pb: 3}}>
        {renderStepContent()} 
        </Box>
    </Container>
  );
}

export default OnboardingPage; 